import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  currentAuditEntryAtom,
  jiraConfigAtom,
  reportDemoModeAtom,
  userAtom,
} from '@/global-store.ts';
import { ReportLicenseExpiredBanner } from '@layout/ReportLicenseExpiredBanner.jsx';
import { JiraConfigExpiredBanner } from '@layout/JiraConfigExpiredBanner.jsx';
import { LastAuditTooOldBanner } from '@layout/LastAuditTooOldBanner.jsx';
import DemoModeBanner from '@components/DemoModeBanner.jsx';
import { Fragment } from 'react';

export function ReportAreaContent({ isError }) {
  const { t } = useTranslation();
  const location = useLocation();

  const path = location.pathname.endsWith('/')
    ? location.pathname.slice(0, -1)
    : location.pathname;

  const [user] = useAtom(userAtom);
  const [reportDemoMode] = useAtom(reportDemoModeAtom);
  const [currentAuditEntry] = useAtom(currentAuditEntryAtom);
  const [jiraConfig] = useAtom(jiraConfigAtom);

  const reportLicenseExpired = currentAuditEntry?.endDate
    ? new Date(currentAuditEntry.endDate).setHours(23, 59, 59) < new Date()
    : false;

  const showReportErrorBanner = !path.startsWith('/report/access/');

  const isTodosEnabledArea = [
    'issues',
    'pdf',
    'pages',
    'my-goals',
    'analysis/broken-links',
    'analysis/spellchecker',
  ].some((module) => path.startsWith(`/report/${module}`));

  const showJiraConfigError =
    jiraConfig && !jiraConfig.isValid && isTodosEnabledArea;

  if (isError && path !== '/report/settings') {
    return (
      <Fragment>
        <Outlet />

        <Grid
          container
          sx={{
            alignItems: 'center',
            gap: 5,
            justifyContent: 'center',
            p: 4,
            background: 'white',
            borderRadius: 3,
          }}
        >
          <Grid size={12}>
            <Typography variant='h2'>{t('noReportAvailable')}</Typography>
          </Grid>
        </Grid>
      </Fragment>
    );
  }

  const renderBanner = () => {
    if (reportDemoMode) {
      return <DemoModeBanner product='Report' />;
    }
    if (!showReportErrorBanner) {
      return null;
    }
    if (reportLicenseExpired) {
      return <ReportLicenseExpiredBanner />;
    }
    return <LastAuditTooOldBanner />;
  };

  const shouldHideOutlet =
    reportLicenseExpired &&
    user.role === 'customer' &&
    path !== '/report/overview';

  return (
    <div className='eye:space-y-s'>
      {renderBanner()}
      {showJiraConfigError && <JiraConfigExpiredBanner />}
      {!shouldHideOutlet && (
        // div makes sure `eye:space-y-s` is not applied to children of Outlet
        <div>
          <Outlet />
        </div>
      )}
    </div>
  );
}
